import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Copy, ExternalLink, Printer, RotateCcw, Send, Truck } from "lucide-react";
import JsBarcode from "jsbarcode";
import { toast } from "sonner";
import { api, eur, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const COURIERS = ["BRT", "GLS", "SDA", "Poste"];

const STATUS_OPTIONS = [
  { value: "processing", label: "Pagato" },
  { value: "shipped", label: "Spedito" },
  { value: "delivered", label: "Consegnato" },
];

const RETURN_STATUS = {
  requested: "Richiesta di reso in attesa",
  approved: "Reso approvato — in attesa di rientro",
  rejected: "Reso rifiutato",
  refunded: "Rimborsato",
};

function Block({ title, children, testId }) {
  return (
    <div className="border border-white/10 bg-card p-7 md:p-8" data-testid={testId}>
      <p className="text-xs uppercase tracking-[0.25em] text-primary mb-6">{title}</p>
      {children}
    </div>
  );
}

function Row({ label, children }) {
  return (
    <div className="flex justify-between gap-6 py-3 border-b border-white/5 last:border-0">
      <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground flex-shrink-0">{label}</span>
      <span className="text-sm text-right break-all">{children || "—"}</span>
    </div>
  );
}

export default function AdminOrder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [order, setOrder] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [status, setStatus] = useState("processing");
  const [courier, setCourier] = useState("");
  const [tracking, setTracking] = useState("");
  const [notify, setNotify] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get(`/admin/orders/${id}`)
      .then((r) => {
        setOrder(r.data);
        setStatus(r.data.fulfillment_status && ["processing", "shipped", "delivered"].includes(r.data.fulfillment_status)
          ? r.data.fulfillment_status : "processing");
        setCourier(r.data.courier || "");
        setTracking(r.data.tracking_number || "");
      })
      .catch(() => setNotFound(true));
  }, [id]);

  useEffect(() => {
    if (user && user.role === "admin") load();
  }, [user, load]);

  if (user === null) {
    return (
      <main className="pt-40 min-h-screen max-w-[1600px] mx-auto px-6 md:px-12">
        <p className="text-muted-foreground">Verifica accesso…</p>
      </main>
    );
  }

  if (!user || user.role !== "admin") {
    return (
      <main className="pt-40 min-h-screen max-w-[1600px] mx-auto px-6 md:px-12" data-testid="admin-order-denied">
        <h1 className="font-serif font-light text-4xl mb-6">Accesso riservato</h1>
        <p className="text-muted-foreground mb-8">Accedi come amministratore per vedere questo ordine.</p>
        <Link to="/admin" className="text-primary text-sm tracking-wide" data-testid="admin-order-login-link">
          Vai al login admin
        </Link>
      </main>
    );
  }

  if (notFound) {
    return (
      <main className="pt-40 min-h-screen max-w-[1600px] mx-auto px-6 md:px-12" data-testid="admin-order-not-found">
        <p className="text-muted-foreground">Ordine non trovato.</p>
        <Link to="/admin" className="text-primary text-sm mt-4 inline-block">Torna al pannello</Link>
      </main>
    );
  }

  if (!order) {
    return (
      <main className="pt-40 min-h-screen max-w-[1600px] mx-auto px-6 md:px-12">
        <p className="text-muted-foreground">Caricamento ordine…</p>
      </main>
    );
  }

  const c = order.customer || {};
  const fullAddress = [c.street, `${c.zip || ""} ${c.city || ""} (${c.province || ""})`.trim(), "Italia"]
    .filter(Boolean).join(", ");
  const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}`;

  const copyAddress = async () => {
    const text = `${c.name || ""} ${c.surname || ""}\n${c.street || ""}\n${c.zip || ""} ${c.city || ""} (${c.province || ""})\nItalia${c.phone ? `\nTel: ${c.phone}` : ""}`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Indirizzo copiato per l'etichetta");
    } catch {
      toast.error("Copia non riuscita");
    }
  };

  const escapeHtml = (s) =>
    String(s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

  const printLabel = () => {
    const win = window.open("", "_blank", "width=430,height=660");
    if (!win) {
      toast.error("Il browser ha bloccato la finestra di stampa: abilita i popup");
      return;
    }
    const orderCode = escapeHtml(order.id.slice(0, 8).toUpperCase());
    const recipientName = escapeHtml(`${c.name || ""} ${c.surname || ""}`.trim());
    const cityLine = escapeHtml(`${c.zip || ""} ${c.city || ""}${c.province ? ` (${c.province})` : ""}`.trim());
    const country = escapeHtml(c.country || "Italia");
    let barcodeSvg = "";
    const trackingCode = (tracking || order.tracking_number || "").trim();
    if (trackingCode) {
      try {
        const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
        JsBarcode(svg, trackingCode, {
          format: "CODE128",
          displayValue: true,
          fontSize: 15,
          height: 46,
          margin: 0,
          textMargin: 2,
        });
        const bw = parseFloat(svg.getAttribute("width"));
        const bh = parseFloat(svg.getAttribute("height"));
        svg.setAttribute("viewBox", `0 -2 ${bw} ${bh + 10}`);
        svg.removeAttribute("width");
        svg.removeAttribute("height");
        svg.setAttribute("style", "width:80%;height:auto;display:block;margin:0 auto;");
        barcodeSvg = svg.outerHTML;
      } catch {
        barcodeSvg = "";
      }
    }
    win.document.write(`<!DOCTYPE html>
<html lang="it">
<head>
<meta charset="utf-8" />
<title>Etichetta ordine #${orderCode}</title>
<style>
@page { size: 100mm 150mm; margin: 0; }
* { margin: 0; padding: 0; box-sizing: border-box; }
html, body { width: 100mm; height: 150mm; }
body {
  font-family: Helvetica, Arial, sans-serif;
  color: #000;
  background: #fff;
  padding: 8mm;
  position: relative;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}
.tag { font-size: 7pt; letter-spacing: 0.3em; text-transform: uppercase; margin-bottom: 2mm; }
.sender { font-size: 9pt; line-height: 1.45; }
.sender .brand { font-weight: bold; }
hr { border: none; border-top: 0.4mm solid #000; margin: 5mm 0; }
.dest { flex: 1; display: flex; flex-direction: column; justify-content: center; }
.recipient-name { font-size: 17pt; font-weight: bold; line-height: 1.2; margin-bottom: 2mm; }
.recipient { font-size: 12pt; line-height: 1.5; }
.phone { font-size: 10pt; margin-top: 2.5mm; }
.order-ref { position: absolute; top: 5mm; right: 8mm; font-size: 7pt; letter-spacing: 0.05em; }
</style>
</head>
<body>
<div>
  <p class="tag">Mittente</p>
  <p class="sender"><span class="brand">AB fëlise shop</span><br />Via Mare, 62<br />89030 Condofuri (R.C.)<br />Italia</p>
</div>
<hr />
<div class="dest">
  <p class="tag">Destinatario</p>
  <p class="recipient-name">${recipientName}</p>
  <p class="recipient">${escapeHtml(c.street)}<br />${cityLine}<br />${country}</p>
  ${c.phone ? `<p class="phone">Tel: ${escapeHtml(c.phone)}</p>` : ""}
</div>
${barcodeSvg ? `<div style="margin:10mm 0 4mm 0">${barcodeSvg}</div>` : ""}
<p class="order-ref">Ordine #${orderCode}</p>
</body>
</html>`);
    win.document.close();
    win.focus();
    setTimeout(() => {
      win.print();
      win.onafterprint = () => win.close();
    }, 300);
  };

  const save = async (extra = {}) => {
    setBusy(true);
    try {
      const { data } = await api.patch(`/admin/orders/${order.id}`, {
        fulfillment_status: status,
        courier: courier || null,
        tracking_number: tracking || null,
        notify_customer: notify,
        ...extra,
      });
      setOrder({ ...data, return_request: order.return_request });
      toast.success(extra.notify_customer ? "Ordine aggiornato e email di tracking inviata" : "Ordine aggiornato");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const markShipped = () => {
    setStatus("shipped");
    save({ fulfillment_status: "shipped", notify_customer: notify && !!tracking });
  };

  const decideReturn = async (approve) => {
    if (!window.confirm(approve ? "Approvare il reso?" : "Rifiutare il reso?")) return;
    try {
      await api.post(`/admin/returns/${order.return_request.id}/${approve ? "approve" : "reject"}`);
      toast.success(approve ? "Reso approvato — email inviata al cliente" : "Reso rifiutato — email inviata al cliente");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const displayStatus = order.payment_status !== "paid"
    ? (order.payment_status === "pending" ? "In attesa di pagamento" : order.payment_status)
    : (order.fulfillment_status === "shipped" ? "Spedito"
      : order.fulfillment_status === "delivered" ? "Consegnato"
      : order.fulfillment_status === "return_requested" ? "Reso richiesto"
      : order.fulfillment_status === "return_approved" ? "Reso approvato"
      : "Pagato");

  const ret = order.return_request;

  return (
    <main className="pt-20 min-h-screen" data-testid="admin-order-page">
      <div className="max-w-[1200px] mx-auto px-6 md:px-12 pt-14 md:pt-20 pb-24">
        <button
          onClick={() => navigate("/admin")}
          data-testid="admin-order-back"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-muted-foreground hover:text-primary transition-colors mb-10"
        >
          <ArrowLeft size={14} /> Ordini
        </button>

        <div className="flex flex-wrap items-end justify-between gap-6 mb-12">
          <div>
            <p className="overline-label mb-3">Ordine</p>
            <h1 className="font-serif font-light text-4xl md:text-5xl" data-testid="admin-order-title">
              #{order.id.slice(0, 8).toUpperCase()}
            </h1>
          </div>
          <span
            className={`text-xs uppercase tracking-[0.25em] ${order.payment_status === "paid" ? "text-primary" : "text-muted-foreground"}`}
            data-testid="admin-order-status"
          >
            {displayStatus}
          </span>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          <Block title="A — Cliente" testId="block-customer">
            <Row label="Nome">{`${c.name || ""} ${c.surname || ""}`.trim()}</Row>
            <Row label="Email">{c.email}</Row>
            <Row label="Telefono">{c.phone}</Row>
            <Row label="Data ordine">
              {order.created_at ? new Date(order.created_at).toLocaleString("it-IT") : "—"}
            </Row>
          </Block>

          <Block title="B — Indirizzo di spedizione" testId="block-shipping">
            <p className="font-serif text-xl mb-1">{c.name} {c.surname}</p>
            <p className="text-sm text-muted-foreground leading-relaxed mb-2" data-testid="shipping-address">
              {c.street}<br />
              {c.zip} {c.city} ({c.province})<br />
              Italia
            </p>
            {c.delivery_notes && (
              <p className="text-sm text-primary mb-2" data-testid="delivery-notes">Note: {c.delivery_notes}</p>
            )}
            {c.phone && <p className="text-sm text-muted-foreground mb-4">Tel: {c.phone}</p>}
            <div className="flex flex-wrap gap-3 mt-5">
              <button
                onClick={copyAddress}
                data-testid="copy-address-btn"
                className="inline-flex items-center gap-2 h-11 px-5 rounded-full border border-white/15 text-xs uppercase tracking-[0.2em] text-muted-foreground hover:border-primary hover:text-primary transition-colors"
              >
                <Copy size={13} /> Copia indirizzo per etichetta
              </button>
              <a
                href={mapsUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="maps-link"
                className="inline-flex items-center gap-2 h-11 px-5 rounded-full border border-white/15 text-xs uppercase tracking-[0.2em] text-muted-foreground hover:border-primary hover:text-primary transition-colors"
              >
                <ExternalLink size={13} /> Apri su Google Maps
              </a>
            </div>
          </Block>

          <Block title="C — Indirizzo di fatturazione" testId="block-billing">
            <p className="text-sm text-muted-foreground">Uguale all'indirizzo di spedizione.</p>
          </Block>

          <Block title="D — Ordine" testId="block-order">
            <div className="space-y-3 mb-6">
              {(order.items || []).map((i, idx) => (
                <div key={idx} className="flex justify-between gap-4 text-sm">
                  <span>{i.name} ×{i.quantity}</span>
                  <span>{eur(i.price * i.quantity)}</span>
                </div>
              ))}
            </div>
            <Row label="Spese spedizione">Incluse</Row>
            <Row label="Metodo pagamento">Carta (Stripe)</Row>
            <Row label="Totale">{eur(order.total || 0)}</Row>

            <div className="grid sm:grid-cols-3 gap-4 mt-8">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Stato</Label>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger className="bg-secondary border-white/10" data-testid="order-status-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121214] border-white/10 text-foreground">
                    {STATUS_OPTIONS.map((s) => (
                      <SelectItem key={s.value} value={s.value} className="focus:bg-white/5 focus:text-foreground">
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Corriere</Label>
                <Select value={courier} onValueChange={setCourier}>
                  <SelectTrigger className="bg-secondary border-white/10" data-testid="order-courier-select">
                    <SelectValue placeholder="Scegli…" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121214] border-white/10 text-foreground">
                    {COURIERS.map((cr) => (
                      <SelectItem key={cr} value={cr} className="focus:bg-white/5 focus:text-foreground">
                        {cr}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Numero tracking</Label>
                <Input
                  data-testid="order-tracking-input"
                  value={tracking}
                  onChange={(e) => setTracking(e.target.value)}
                  className="bg-secondary border-white/10 focus-visible:ring-primary"
                />
              </div>
            </div>
            {order.tracking_link && (
              <p className="text-xs text-muted-foreground mt-4 break-all" data-testid="tracking-link-preview">
                Link tracking: <a href={order.tracking_link} target="_blank" rel="noopener noreferrer" className="text-primary">{order.tracking_link}</a>
              </p>
            )}
            <label className="flex items-center gap-3 cursor-pointer mt-5" data-testid="notify-label">
              <Checkbox
                data-testid="notify-checkbox"
                checked={notify}
                onCheckedChange={(v) => setNotify(!!v)}
                className="border-white/20 data-[state=checked]:bg-primary data-[state=checked]:border-primary"
              />
              <span className="text-sm text-muted-foreground">Notifica il cliente via email quando aggiungi il tracking</span>
            </label>
            <button
              onClick={() => save()}
              disabled={busy}
              data-testid="order-save-btn"
              className="mt-6 h-12 px-8 rounded-full bg-primary text-primary-foreground text-xs uppercase tracking-[0.2em] font-medium hover:bg-[#c8ad8f] transition-colors duration-300 disabled:opacity-50"
            >
              {busy ? "Salvataggio…" : "Salva modifiche"}
            </button>
          </Block>

          <Block title="E — Azioni" testId="block-actions">
            <div className="flex flex-col gap-3">
              <button
                onClick={printLabel}
                data-testid="print-btn"
                className="inline-flex items-center gap-3 h-12 px-6 rounded-full border border-white/15 text-xs uppercase tracking-[0.2em] text-muted-foreground hover:border-primary hover:text-primary transition-colors"
              >
                <Printer size={14} /> Stampa etichetta
              </button>
              <button
                onClick={() => save({ notify_customer: true })}
                disabled={busy || !tracking || !courier}
                data-testid="send-tracking-btn"
                className="inline-flex items-center gap-3 h-12 px-6 rounded-full border border-white/15 text-xs uppercase tracking-[0.2em] text-muted-foreground hover:border-primary hover:text-primary transition-colors disabled:opacity-40"
              >
                <Send size={14} /> Invia email tracking al cliente
              </button>
              <button
                onClick={markShipped}
                disabled={busy}
                data-testid="mark-shipped-btn"
                className="inline-flex items-center gap-3 h-12 px-6 rounded-full bg-primary text-primary-foreground text-xs uppercase tracking-[0.2em] font-medium hover:bg-[#c8ad8f] transition-colors duration-300 disabled:opacity-50"
              >
                <Truck size={14} /> Segna come spedito
              </button>
            </div>
          </Block>
        </div>

        {ret && (
          <div className="border border-primary/40 bg-primary/5 p-7 md:p-8 mt-6" data-testid="return-section">
            <p className="text-xs uppercase tracking-[0.25em] text-primary mb-6 flex items-center gap-2">
              <RotateCcw size={13} /> Reso — {RETURN_STATUS[ret.status] || ret.status}
            </p>
            <div className="space-y-3 mb-6">
              {ret.items.map((i, idx) => (
                <div key={idx} className="text-sm">
                  <span>{i.name} ×{i.quantity}</span>
                  <span className="text-muted-foreground"> — {i.reason}{i.note ? ` · ${i.note}` : ""}</span>
                </div>
              ))}
            </div>
            {ret.photo && (
              <img src={ret.photo} alt="Foto reso" className="max-w-xs border border-white/10 mb-6" data-testid="return-photo" />
            )}
            {ret.status === "requested" && (
              <div className="flex gap-3">
                <button
                  onClick={() => decideReturn(true)}
                  data-testid="return-approve-btn"
                  className="h-12 px-8 rounded-full bg-primary text-primary-foreground text-xs uppercase tracking-[0.2em] font-medium hover:bg-[#c8ad8f] transition-colors duration-300"
                >
                  Approva reso
                </button>
                <button
                  onClick={() => decideReturn(false)}
                  data-testid="return-reject-btn"
                  className="h-12 px-8 rounded-full border border-white/15 text-xs uppercase tracking-[0.2em] text-muted-foreground hover:border-destructive hover:text-destructive transition-colors"
                >
                  Rifiuta
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </main>
  );
}
